/**
 * Bundle Options Component
 * Handles dynamic bundle selection with quantity and price updates
 * 
 * @author Shopify Theme Development
 * @version 1.0.0
 */

(() => {
  'use strict';
  
  /**
   * Bundle Options Component
   * Manages product bundle selection with real-time quantity and price updates
   */
  class BundleOptions {
    // Class constants for better maintainability
    static SELECTORS = {
      container: (blockId) => `[data-block-id="${blockId}"]`,
      options: (blockId) => `input[name="bundle-option-${blockId}"]`,
      bundleQuantity: (sectionId) => `#BundleQuantity-${sectionId}`,
      displayQuantity: (sectionId) => `#Quantity-${sectionId}, .quantity__input`,
      quantityComponent: (sectionId) => `quantity-input[data-section="${sectionId}"]`,
      priceBlock: (sectionId) => `#price-${sectionId}`,
      saleBadge: (sectionId) => `#price-${sectionId} .price__badge-sale`,
      klarnaText: '.klarna-text span'
    };

    static PRICE_SELECTORS = [
      's.price-item.price-item--regular',
      's .price-item--regular', 
      '.price-item--regular',
      's .price-item',
      's'
    ];

    static SALE_PRICE_SELECTORS = [
      '.price-item--sale',
      '.price-item--last'
    ];

    static EVENTS = {
      BUNDLE_QUANTITY_CHANGED: 'bundle:quantity-changed',
      QUANTITY_CHANGED: 'quantity:changed'
    };

    /**
     * Initialize Bundle Options
     * @param {string} blockId - Block identifier
     * @param {string} sectionId - Section identifier
     */
    constructor(blockId, sectionId) {
      this.blockId = blockId;
      this.sectionId = sectionId;
      this.elements = this.#getElements();
      
      if (this.elements.options.length) {
        this.#init();
      }
    }

    /**
     * Get DOM elements
     * @private
     */
    #getElements() {
      const { SELECTORS } = BundleOptions;
      return {
        container: document.querySelector(SELECTORS.container(this.blockId)),
        options: document.querySelectorAll(SELECTORS.options(this.blockId)),
        bundleQuantityInput: document.querySelector(SELECTORS.bundleQuantity(this.sectionId)),
        displayQuantityInputs: document.querySelectorAll(SELECTORS.displayQuantity(this.sectionId)),
        quantityComponent: document.querySelector(SELECTORS.quantityComponent(this.sectionId)),
        priceBlock: document.querySelector(SELECTORS.priceBlock(this.sectionId)),
        saleBadge: document.querySelector(SELECTORS.saleBadge(this.sectionId)),
        klarnaText: document.querySelector(SELECTORS.klarnaText)
      };
    }

    /**
     * Initialize component
     * @private
     */
    #init() {
      this.#bindEvents();
      this.#setInitialQuantity();
    }

    /**
     * Bind event listeners
     * @private
     */
    #bindEvents() {
      // Bundle option changes
      this.elements.options.forEach(option => {
        option.addEventListener('change', this.#handleOptionChange.bind(this));
      });

      // External quantity changes
      this.elements.displayQuantityInputs.forEach(input => {
        input.addEventListener('change', this.#handleExternalQuantityChange.bind(this));
      });
    }

    /**
     * Handle bundle option change
     * @private
     */
    #handleOptionChange(event) {
      if (!event.target.checked) return;
      
      const quantity = parseInt(event.target.closest('.bundle-option').dataset.quantity, 10);
      this.#updateQuantity(quantity);
      this.#addVisualFeedback();
    }

    /**
     * Handle external quantity input change
     * @private
     */
    #handleExternalQuantityChange(event) {
      const newQuantity = parseInt(event.target.value, 10);
      
      if (this.elements.bundleQuantityInput?.value !== newQuantity.toString()) {
        if (this.elements.bundleQuantityInput) {
          this.elements.bundleQuantityInput.value = newQuantity;
        }
        this.#syncBundleOptionSelection(newQuantity);
      }
    }

    /**
     * Update quantity across all inputs and systems
     * @private
     */
    #updateQuantity(quantity) {
      // Update all quantity inputs
      this.#updateQuantityInputs(quantity);
      
      // Update Web Component
      this.#updateQuantityComponent(quantity);
      
      // Shopify integration
      this.#publishQuantityUpdate(quantity);
      
      // Update displays
      this.#updateQuantityDisplays(quantity);
      
      // Price updates
      this.#updatePrices(quantity);
    }

    /**
     * Update quantity input elements
     * @private
     */
    #updateQuantityInputs(quantity) {
      // Bundle quantity input (for cart)
      if (this.elements.bundleQuantityInput) {
        this.elements.bundleQuantityInput.value = quantity;
        this.#dispatchEvents(this.elements.bundleQuantityInput);
      }

      // Display quantity inputs (for UI)
      this.elements.displayQuantityInputs.forEach(input => {
        if (input && input !== this.elements.bundleQuantityInput) {
          input.value = quantity;
          this.#dispatchEvents(input);
        }
      });
    }

    /**
     * Update quantity Web Component
     * @private
     */
    #updateQuantityComponent(quantity) {
      const input = this.elements.quantityComponent?.querySelector('input');
      if (input) {
        input.value = quantity;
        this.#dispatchEvents(input);
      }
    }

    /**
     * Publish quantity update to Shopify pub/sub system
     * @private
     */
    #publishQuantityUpdate(quantity) {
      window.publish?.(window.PUB_SUB_EVENTS?.quantityUpdate, {
        quantity,
        sectionId: this.sectionId,
        source: 'bundle-options'
      });
    }

    /**
     * Update quantity display elements
     * @private
     */
    #updateQuantityDisplays(quantity) {
      document.querySelectorAll('[data-quantity-display]').forEach(display => {
        display.textContent = quantity;
      });
    }

    /**
     * Update all price-related elements
     * @private
     */
    #updatePrices(quantity) {
      this.#updateMainPrice();
      this.#updateSaleBadge();
      this.#updateKlarnaText();
      this.#dispatchCustomEvents(quantity);
    }

    /**
     * Update main product price block
     * @private
     */
    #updateMainPrice() {
      const selectedOption = this.#getSelectedOption();
      if (!selectedOption) return;

      const bundleOption = selectedOption.closest('.bundle-option');
      const comparePrice = bundleOption.querySelector('.bundle-option__compare-price');
      const regularPrice = bundleOption.querySelector('.bundle-option__price');

      if (!comparePrice || !regularPrice || !this.elements.priceBlock) return;

      this.#updatePriceElements(
        comparePrice.textContent.trim(),
        regularPrice.textContent.trim()
      );
    }

    /**
     * Update price elements in main price block
     * @private
     */
    #updatePriceElements(comparePriceText, regularPriceText) {
      // Update compare price (crossed out)
      const mainComparePrice = this.#findElement(this.elements.priceBlock, BundleOptions.PRICE_SELECTORS);
      if (mainComparePrice) {
        mainComparePrice.textContent = comparePriceText;
      }

      // Update sale price
      const mainSalePrice = this.#findElement(this.elements.priceBlock, BundleOptions.SALE_PRICE_SELECTORS);
      if (mainSalePrice) {
        mainSalePrice.textContent = regularPriceText;
      }

      // Fallback for regular price
      if (!mainSalePrice) {
        const mainRegularPrice = this.elements.priceBlock.querySelector('.price-item:not(.price-item--sale):not(.price-item--regular)');
        if (mainRegularPrice) {
          mainRegularPrice.textContent = regularPriceText;
        }
      }
    }

    /**
     * Update sale percentage badge
     * @private
     */
    #updateSaleBadge() {
      if (!this.elements.saleBadge) return;

      const selectedOption = this.#getSelectedOption();
      if (!selectedOption) return;

      const bundleOption = selectedOption.closest('.bundle-option');
      const comparePrice = bundleOption.querySelector('.bundle-option__compare-price');
      const regularPrice = bundleOption.querySelector('.bundle-option__price');

      if (!comparePrice || !regularPrice) {
        // Hide badge if no sale prices available
        this.elements.saleBadge.style.display = 'none';
        return;
      }

      const comparePriceValue = this.#extractPriceValue(comparePrice.textContent);
      const regularPriceValue = this.#extractPriceValue(regularPrice.textContent);

      if (!comparePriceValue || !regularPriceValue || comparePriceValue <= regularPriceValue) {
        // Hide badge if no valid sale
        this.elements.saleBadge.style.display = 'none';
        return;
      }

      // Calculate sale percentage: (compare_price - sale_price) / compare_price * 100
      const salePercentage = Math.round(((comparePriceValue - regularPriceValue) / comparePriceValue) * 100);
      
      // Show badge and update text
      this.elements.saleBadge.style.display = '';
      const saleText = window.theme?.translations?.sale || 'Sale';
      this.elements.saleBadge.textContent = `${saleText} ${salePercentage}%`;
    }

    /**
     * Update Klarna payment text
     * @private
     */
    #updateKlarnaText() {
      if (!this.elements.klarnaText) return;

      const selectedOption = this.#getSelectedOption();
      if (!selectedOption) return;

      const bundleOption = selectedOption.closest('.bundle-option');
      const regularPrice = bundleOption.querySelector('.bundle-option__price');
      if (!regularPrice) return;

      const priceValue = this.#extractPriceValue(regularPrice.textContent);
      if (!priceValue) return;

      const months = this.#extractMonths(this.elements.klarnaText.textContent);
      const monthlyAmount = priceValue / months;
      const formattedAmount = this.#formatCurrency(monthlyAmount, regularPrice.textContent);

      // Use translation if available, fallback to English
      const klarnaTemplate = window.theme?.translations?.klarnaText || 'As low as {{amount}}/mo. with';
      this.elements.klarnaText.textContent = klarnaTemplate.replace('{{amount}}', formattedAmount);
    }

    /**
     * Extract numeric price value from text
     * @private
     */
    #extractPriceValue(priceText) {
      const cleanText = priceText.trim().replace(/[€$£¥]/g, '');
      const match = cleanText.match(/[\d,\.]+/);
      
      if (!match) return null;
      
      const numberStr = match[0];
      
      // Handle European format (€199,90) vs US format ($199.90)
      if (numberStr.includes(',') && numberStr.lastIndexOf(',') > numberStr.lastIndexOf('.')) {
        return parseFloat(numberStr.replace(/\./g, '').replace(',', '.'));
      }
      
      return parseFloat(numberStr.replace(/,/g, ''));
    }

    /**
     * Extract months from Klarna text
     * @private
     */
    #extractMonths(klarnaText) {
      const match = klarnaText.match(/(\d+)\s*mo\./);
      return match ? parseInt(match[1], 10) : 3;
    }

    /**
     * Format currency amount
     * @private
     */
    #formatCurrency(amount, originalText) {
      const formatted = amount.toFixed(2);
      
      if (originalText.includes('€')) {
        return originalText.includes('EUR') ? `€${formatted} EUR` : `€${formatted}`;
      }
      
      return originalText.includes('$') ? `$${formatted}` : `€${formatted}`;
    }

    /**
     * Dispatch custom events for price updates
     * @private
     */
    #dispatchCustomEvents(quantity) {
      // Bundle quantity changed event
      document.dispatchEvent(new CustomEvent(BundleOptions.EVENTS.BUNDLE_QUANTITY_CHANGED, {
        detail: { quantity, bundleId: this.blockId, sectionId: this.sectionId },
        bubbles: true
      }));

      // Price per item updates
      document.querySelectorAll('[data-price-per-item]').forEach(element => {
        element.dispatchEvent(new CustomEvent(BundleOptions.EVENTS.QUANTITY_CHANGED, {
          detail: { quantity }
        }));
      });
    }

    /**
     * Sync bundle option selection with external quantity
     * @private
     */
    #syncBundleOptionSelection(quantity) {
      const matchingOption = Array.from(this.elements.options).find(option => 
        parseInt(option.closest('.bundle-option').dataset.quantity, 10) === quantity
      );

      if (matchingOption && !matchingOption.checked) {
        matchingOption.checked = true;
      }
    }

    /**
     * Set initial quantity on component load
     * @private
     */
    #setInitialQuantity() {
      const checkedOption = this.#getSelectedOption();
      if (checkedOption) {
        const quantity = parseInt(checkedOption.closest('.bundle-option').dataset.quantity, 10);
        this.#updateQuantity(quantity);
      }
    }

    /**
     * Add visual feedback during updates
     * @private
     */
    #addVisualFeedback() {
      if (!this.elements.container) return;

      this.elements.container.classList.add('bundle-options--updating');
      setTimeout(() => {
        this.elements.container.classList.remove('bundle-options--updating');
      }, 150);
    }

    /**
     * Get currently selected option
     * @private
     */
    #getSelectedOption() {
      return document.querySelector(`input[name="bundle-option-${this.blockId}"]:checked`);
    }

    /**
     * Find element using multiple selectors
     * @private
     */
    #findElement(parent, selectors) {
      return selectors.reduce((found, selector) => found || parent.querySelector(selector), null);
    }

    /**
     * Dispatch events on element
     * @private
     */
    #dispatchEvents(element) {
      ['change', 'input'].forEach(eventType => {
        element.dispatchEvent(new Event(eventType, { bubbles: true }));
      });
    }
  }

  // Expose to global scope
  window.BundleOptions = BundleOptions;
})();
